/**
 * The screen in the dining room. It has one job: tell a guest whether their number is
 * still being made or ready to collect, from across a room, at a glance, without them
 * having to ask anyone.
 *
 * Everything here is a setting because the screen hangs in a different room in every
 * restaurant — some are bright, some are far away, some are turned sideways.
 */

export type OrderDisplayConfig = {
  headingPreparing: string
  headingReady: string
  backgroundColor: string
  textColor: string
  accentColor: string
  readyColor: string
  logoUrl: string | null
  showLogo: boolean
  fontFamily: string
  /** How big an order number is, as a percentage of what fits the column. */
  numberScale: number
  headingScale: number
  columnsPerSide: number
  /** A number just moved to ready pulses for this long, so a guest notices the change. */
  highlightNewlyReadySeconds: number
  chimeOnReady: boolean
  chimeVolume: number
  pollIntervalSeconds: number
  showChannelBadges: boolean
}

export const DEFAULT_ORDER_DISPLAY: OrderDisplayConfig = {
  headingPreparing: 'Preparing',
  headingReady: 'Ready to collect',
  backgroundColor: '#0b0b0f',
  textColor: '#ffffff',
  accentColor: '#702082',
  readyColor: '#14a44d',
  logoUrl: null,
  showLogo: true,
  fontFamily: 'system',
  numberScale: 100,
  headingScale: 100,
  columnsPerSide: 2,
  highlightNewlyReadySeconds: 30,
  chimeOnReady: true,
  chimeVolume: 70,
  pollIntervalSeconds: 5,
  showChannelBadges: false,
}

type Row = { [key: string]: unknown } | null | undefined

function text(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim() ? value : fallback
}
function color(value: unknown, fallback: string) {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback
}
function clamped(value: unknown, fallback: number, min: number, max: number) {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback
}
function bool(value: unknown, fallback: boolean) {
  return typeof value === 'boolean' ? value : fallback
}

/**
 * A screen with no record still works. A colour written as something that is not a
 * colour falls back rather than painting the room invisible — the worst failure here is
 * a screen a guest cannot read.
 */
export function readOrderDisplayConfig(row: Row): OrderDisplayConfig {
  if (!row) return DEFAULT_ORDER_DISPLAY
  const d = DEFAULT_ORDER_DISPLAY
  return {
    headingPreparing: text(row.headingPreparing, d.headingPreparing),
    headingReady: text(row.headingReady, d.headingReady),
    backgroundColor: color(row.backgroundColor, d.backgroundColor),
    textColor: color(row.textColor, d.textColor),
    accentColor: color(row.accentColor, d.accentColor),
    readyColor: color(row.readyColor, d.readyColor),
    logoUrl: typeof row.logoUrl === 'string' && row.logoUrl.startsWith('https://') ? row.logoUrl : null,
    showLogo: bool(row.showLogo, d.showLogo),
    fontFamily: typeof row.fontFamily === 'string' ? row.fontFamily : d.fontFamily,
    numberScale: clamped(row.numberScale, d.numberScale, 50, 300),
    headingScale: clamped(row.headingScale, d.headingScale, 50, 300),
    columnsPerSide: clamped(row.columnsPerSide, d.columnsPerSide, 1, 6),
    highlightNewlyReadySeconds: clamped(row.highlightNewlyReadySeconds, d.highlightNewlyReadySeconds, 0, 600),
    chimeOnReady: bool(row.chimeOnReady, d.chimeOnReady),
    chimeVolume: clamped(row.chimeVolume, d.chimeVolume, 0, 100),
    pollIntervalSeconds: clamped(row.pollIntervalSeconds, d.pollIntervalSeconds, 2, 120),
    showChannelBadges: bool(row.showChannelBadges, d.showChannelBadges),
  }
}

export type DisplayOrder = {
  id: string
  humanOrderCode?: string | null
  createdAt?: string | null
  orderType?: string | null
  readyForPickup?: boolean | null
  transactionChannel?: string | null
}

export type OrderBoard = { preparing: DisplayOrder[]; ready: DisplayOrder[] }

/**
 * One order can be on several kitchen displays at once; the guest has one number. An
 * order counts as ready only when every station holding a piece of it says so, for the
 * same reason the kitchen screen does — otherwise a number is called while half of it
 * is still being made.
 */
export function board(displays: { slips?: (DisplayOrder | null)[] | null }[]): OrderBoard {
  const byId = new Map<string, DisplayOrder>()
  for (const display of displays)
    for (const slip of display.slips ?? []) {
      if (!slip) continue
      const existing = byId.get(slip.id)
      if (!existing) {
        byId.set(slip.id, { ...slip })
        continue
      }
      if (slip.readyForPickup !== true) existing.readyForPickup = false
    }
  const all = [...byId.values()].sort((a, b) => Date.parse(a.createdAt ?? '') - Date.parse(b.createdAt ?? '') || a.id.localeCompare(b.id))
  return {
    preparing: all.filter((order) => order.readyForPickup !== true),
    ready: all.filter((order) => order.readyForPickup === true),
  }
}

/**
 * Numbers that have just become ready. The first look announces nothing — a screen
 * switched on mid-service should not chime for every order already waiting.
 */
export function newlyReady(previous: Set<string> | null, ready: DisplayOrder[]): string[] {
  if (previous === null) return []
  return ready.map((order) => order.id).filter((id) => !previous.has(id))
}

export const ORDER_DISPLAY_FONTS: Record<string, string> = {
  system: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  inter: 'Inter, system-ui, sans-serif',
  roboto: 'Roboto, system-ui, sans-serif',
  helvetica: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  arial: 'Arial, Helvetica, sans-serif',
  georgia: "Georgia, 'Times New Roman', serif",
  times: "'Times New Roman', Times, serif",
  courier: "'Courier New', Courier, monospace",
}
